/**
 * Survey Management API Service
 * Handles all API calls related to surveys
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nh9pshlcw7.execute-api.us-west-2.amazonaws.com/prod';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Set access token getter function
 */
export const setTokenGetter = (getter) => {
  apiClient.interceptors.request.use(async (config) => {
    const token = await getter();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
};

// ============================================================================
// COMPETENCIES ENDPOINTS
// ============================================================================

/**
 * Get all competencies
 */
export const getCompetencies = async () => {
  const response = await apiClient.get('/leading/api/competencies');
  return response.data;
};

// ============================================================================
// SURVEY ENDPOINTS
// ============================================================================

/**
 * Get all surveys
 */
export const getSurveys = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.cohort_id) params.append('cohort_id', filters.cohort_id);
  if (filters.survey_type) params.append('survey_type', filters.survey_type);
  if (filters.status) params.append('status', filters.status);
  
  const response = await apiClient.get(`/leading/api/surveys?${params}`);
  return response.data;
};

/**
 * Get surveys grouped by cohort with pagination
 */
export const getSurveysGroupedByCohort = async (filters = {}) => {
  const params = new URLSearchParams();
  
  // Status filter (supports comma-separated values)
  if (filters.status) params.append('status', filters.status);
  
  // Pagination parameters
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  
  const response = await apiClient.get(`/leading/api/surveys/grouped-by-cohort?${params}`);
  return response.data;
};

/**
 * Get survey by ID
 */
export const getSurvey = async (surveyId) => {
  const response = await apiClient.get(`/leading/api/surveys/${surveyId}`);
  return response.data;
};

/**
 * Create new survey
 */
export const createSurvey = async (surveyData) => {
  const response = await apiClient.post('/leading/api/surveys', surveyData);
  return response.data;
};

/**
 * Update survey
 */
export const updateSurvey = async (surveyId, surveyData) => {
  const response = await apiClient.put(`/leading/api/surveys/${surveyId}`, surveyData);
  return response.data;
};

/**
 * Delete survey
 */
export const deleteSurvey = async (surveyId) => {
  const response = await apiClient.delete(`/leading/api/surveys/${surveyId}`);
  return response.data;
};

/**
 * Preview survey
 */
export const previewSurvey = async (surveyId) => {
  const response = await apiClient.post(`/leading/api/surveys/${surveyId}/preview`);
  return response.data;
};

/**
 * Preview survey distribution
 */
export const previewDistribution = async (surveyId) => {
  const response = await apiClient.post(`/leading/api/surveys/${surveyId}/preview-distribution`);
  return response.data;
};

/**
 * Send/distribute survey
 * @param {number} surveyId - The survey ID to send
 * @param {object} distributionData - Optional pre-fetched distribution data from preview
 */
export const sendSurvey = async (surveyId, distributionData = null) => {
  const body = distributionData ? { distribution_data: distributionData } : {};
  const response = await apiClient.post(`/leading/api/surveys/${surveyId}/send`, body);
  return response.data;
};

/**
 * Get distribution job status
 * @param {number} surveyId - The survey ID
 * @param {string} jobId - The distribution job ID
 */
export const getDistributionStatus = async (surveyId, jobId) => {
  const response = await apiClient.get(`/leading/api/surveys/${surveyId}/distribution-status/${jobId}`);
  return response.data;
};

/**
 * Get survey versions
 */
export const getSurveyVersions = async (surveyId) => {
  const response = await apiClient.get(`/leading/api/surveys/${surveyId}/versions`);
  return response.data;
};

/**
 * Get specific survey version
 */
export const getSurveyVersion = async (surveyId, version) => {
  const response = await apiClient.get(`/leading/api/surveys/${surveyId}/versions/${version}`);
  return response.data;
};

/**
 * Trigger manual reminder processing for a specific survey
 * @param {number} surveyId - The survey ID to send reminders for
 */
export const triggerReminders = async (surveyId) => {
  const response = await apiClient.post(`/leading/api/surveys/${surveyId}/send-reminders`);
  return response.data;
};

export default {
  getCompetencies,
  getSurveys,
  getSurvey,
  createSurvey,
  updateSurvey,
  deleteSurvey,
  previewSurvey,
  previewDistribution,
  sendSurvey,
  getDistributionStatus,
  getSurveyVersions,
  getSurveyVersion,
  triggerReminders,
  setTokenGetter
};
